/**
 * Sales > Weekly Trend — the workbooks' Promo Tracker and Sales Decomposition:
 * latest-13-week tiles, weekly base vs incremental with year ago, the promo mix,
 * and 13-week blocks side by side. Everything derives from the report's weekly
 * rows, so the page and its export always agree.
 */

import { useMemo } from 'react'
import type { EChartsOption } from 'echarts'

import Chart from '../../components/Chart'
import { DataTable } from '../../components/DataTable'
import { formatCurrency, formatPercent } from '../../lib/format'
import type { CustomReportProps } from '../index'
import {
  chart,
  compactAxis,
  money,
  num,
  colorAt,
  colorsFor,
  records,
  signedPct,
  toneOf,
  type Row,
} from './shared'
import { Empty, Panel, Tile } from './ui'

interface Block {
  label: string
  sales: number
  ya: number
  base: number
  incr: number
  units: number
}

function sum(rows: Row[], key: string): number {
  return rows.reduce((acc, r) => acc + (num(r[key]) ?? 0), 0)
}

function block(label: string, rows: Row[]): Block {
  return {
    label,
    sales: sum(rows, 'dollar_sales'),
    ya: sum(rows, 'dollar_sales_ya'),
    base: sum(rows, 'base_dollars'),
    incr: sum(rows, 'incremental_dollars'),
    units: sum(rows, 'unit_sales'),
  }
}

const chg = (a: number, b: number): number | null => (b ? a / b - 1 : null)

const MIX = [
  ['no_promo_pct', 'No promo'],
  ['tpr_pct', 'Price reduction only'],
  ['feature_pct', 'Feature only'],
  ['display_pct', 'Display only'],
  ['feature_display_pct', 'Feature & display'],
] as const

export function WeeklyTrend({ result, department }: CustomReportProps) {
  const palette6 = colorsFor(department)
  const colors = palette6.list(6)
  const { accent, second, fourth } = palette6
  const weeks = records(result)

  const blocks = useMemo(() => {
    const newestFirst = [...weeks].reverse()
    const names = ['Latest 13 wks', 'Wks 14–26', 'Wks 27–39', 'Wks 40–52']
    return names
      .map((name, i) => block(name, newestFirst.slice(i * 13, i * 13 + 13)))
      .filter((b) => b.sales !== 0 || b.ya !== 0)
  }, [weeks])

  const trendOption = useMemo<EChartsOption>(
    () => ({
      ...chart.baseOption(),
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: unknown) => (typeof v === 'number' ? formatCurrency(v, 'USD', { decimals: 0 }) : '—'),
      },
      xAxis: {
        type: 'category',
        data: weeks.map((w) => String(w.week_ending).slice(5)),
        axisLabel: chart.axisLabel(),
        axisLine: chart.axisLine(),
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { ...chart.axisLabel(), formatter: compactAxis },
        splitLine: chart.splitLine(),
      },
      series: [
        {
          name: 'Base $',
          type: 'bar',
          stack: 'sales',
          data: weeks.map((w) => num(w.base_dollars)),
          itemStyle: { color: accent },
        },
        {
          name: 'Incremental $',
          type: 'bar',
          stack: 'sales',
          data: weeks.map((w) => num(w.incremental_dollars)),
          itemStyle: { color: fourth },
        },
        {
          name: 'Year ago',
          type: 'line',
          smooth: true,
          symbol: 'none',
          data: weeks.map((w) => num(w.dollar_sales_ya)),
          lineStyle: { color: second, width: 2, type: 'dashed' },
          itemStyle: { color: second },
        },
      ],
    }),
    [weeks, accent, second, fourth],
  )

  const mixOption = useMemo<EChartsOption>(() => {
    const totals = MIX.map(([key, name]) => ({
      name,
      value: weeks.reduce((acc, w) => acc + (num(w[key]) ?? 0) * (num(w.dollar_sales) ?? 0), 0),
    }))
    return {
      ...chart.baseOption(),
      tooltip: { trigger: 'item', formatter: '{b}: {d}%' },
      legend: { orient: 'vertical', right: 0, top: 'middle', textStyle: { fontSize: 11 } },
      series: [
        {
          type: 'pie',
          radius: ['48%', '78%'],
          center: ['34%', '50%'],
          label: { show: false },
          data: totals.map((t, i) => ({ ...t, itemStyle: { color: colorAt(colors, i) } })),
        },
      ],
    }
  }, [weeks, colors])

  const blockOption = useMemo<EChartsOption>(
    () => ({
      ...chart.baseOption(),
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: (v: unknown) => (typeof v === 'number' ? formatCurrency(v, 'USD', { decimals: 0 }) : '—'),
      },
      xAxis: {
        type: 'category',
        data: blocks.map((b) => b.label),
        axisLabel: chart.axisLabel(),
        axisLine: chart.axisLine(),
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { ...chart.axisLabel(), formatter: compactAxis },
        splitLine: chart.splitLine(),
      },
      series: [
        {
          name: 'This year',
          type: 'bar',
          barMaxWidth: 34,
          data: blocks.map((b) => b.sales),
          itemStyle: { color: accent },
          label: {
            show: true,
            position: 'top',
            fontSize: 11,
            formatter: (p: { dataIndex: number }) => {
              const b = blocks[p.dataIndex]
              return b === undefined ? '' : signedPct(chg(b.sales, b.ya), 0)
            },
          },
        },
        {
          name: 'Year ago',
          type: 'bar',
          barMaxWidth: 34,
          data: blocks.map((b) => b.ya),
          itemStyle: { color: second, opacity: 0.5 },
        },
      ],
    }),
    [blocks, accent, second],
  )

  if (weeks.length === 0) return <Empty>No weekly data for these filters.</Empty>
  const latest = blocks[0]

  return (
    <div className="space-y-4">
      {latest !== undefined && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile
            label="Sales, latest 13 wks"
            value={money(latest.sales)}
            delta={`${signedPct(chg(latest.sales, latest.ya))} vs YA`}
            deltaTone={toneOf(chg(latest.sales, latest.ya))}
          />
          <Tile
            label="Incremental share"
            value={latest.sales ? formatPercent(latest.incr / latest.sales, 1) : '—'}
            note="of sales from promotion"
            delta=" "
          />
          <Tile
            label="Promo lift"
            value={latest.base ? formatPercent(latest.incr / latest.base, 1) : '—'}
            note="incremental ÷ base"
            delta=" "
          />
          <Tile
            label="Avg price"
            value={latest.units ? formatCurrency(latest.sales / latest.units) : '—'}
            note={`${Math.round(latest.units).toLocaleString()} units`}
            delta=" "
          />
        </div>
      )}
      <div className="grid grid-cols-12 gap-4">
        <Panel wide title="Weekly sales" subtitle="Base and incremental dollars per week, with year ago">
          <Chart option={trendOption} height={320} ariaLabel="Weekly sales base and incremental" />
        </Panel>
        <Panel title="13-week blocks" subtitle="Dollar sales vs year ago; label is % change">
          <Chart option={blockOption} height={300} ariaLabel="Sales by 13-week block" />
        </Panel>
        <Panel title="Promo mix" subtitle="Share of dollar sales by merchandising condition, whole range">
          <Chart option={mixOption} height={300} ariaLabel="Sales by promotion type" />
        </Panel>
      </div>
      <DataTable columns={result.columns} rows={result.rows} caption="Weekly detail" />
    </div>
  )
}
